/**
 * useSmoothStream — soft-reveal buffer for streamed AI text.
 *
 * Phase 51.M+ — the raw stream from Claude arrives in bursts (200-600
 * chars in a single chunk, then a pause). Concatenating on arrival
 * makes the display feel choppy. This hook holds an incoming buffer
 * and reveals characters at a smooth ~40-cps cadence via
 * requestAnimationFrame. If the buffer builds up (fast provider), the
 * reveal rate scales so we never fall meaningfully behind the network.
 *
 * Usage
 * ─────
 *   const { visible, push, flush, reset } = useSmoothStream();
 *   // push('new chunk') as tokens arrive
 *   // <div>{visible}</div>
 *   // call flush() on stream done to reveal any remaining buffered chars
 *   // call reset() when starting a new stream
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const BASE_CPS = 55;          // baseline characters revealed per second
const CATCHUP_MULT = 6;       // if buffer > CATCHUP_THRESHOLD, reveal N x faster
const CATCHUP_THRESHOLD = 40; // buffered chars beyond which we accelerate

export function useSmoothStream(initial = '') {
  const [visible, setVisible] = useState(initial);
  const bufferRef = useRef('');
  const rafRef = useRef(null);
  const lastTickRef = useRef(0);

  const step = useCallback((now) => {
    if (!lastTickRef.current) lastTickRef.current = now;
    const dt = (now - lastTickRef.current) / 1000; // seconds
    lastTickRef.current = now;

    const buffered = bufferRef.current.length;
    if (buffered === 0) {
      rafRef.current = null;
      return;
    }
    // If far behind, catch up faster; otherwise reveal at BASE_CPS.
    const cps = buffered > CATCHUP_THRESHOLD ? BASE_CPS * CATCHUP_MULT : BASE_CPS;
    // Reveal at least 1 char per frame so a slow stream still animates.
    const reveal = Math.max(1, Math.floor(cps * dt));
    const take = Math.min(reveal, buffered);
    const chunk = bufferRef.current.slice(0, take);
    bufferRef.current = bufferRef.current.slice(take);
    setVisible((prev) => prev + chunk);

    rafRef.current = requestAnimationFrame(step);
  }, []);

  const push = useCallback((chunk) => {
    if (!chunk) return;
    bufferRef.current += chunk;
    if (rafRef.current == null) {
      lastTickRef.current = 0;
      rafRef.current = requestAnimationFrame(step);
    }
  }, [step]);

  const flush = useCallback(() => {
    if (bufferRef.current.length) {
      setVisible((prev) => prev + bufferRef.current);
      bufferRef.current = '';
    }
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const reset = useCallback((seed = '') => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    bufferRef.current = '';
    setVisible(seed);
  }, []);

  // Cleanup on unmount.
  useEffect(() => () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
  }, []);

  return { visible, push, flush, reset };
}
